import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Vistas favoritas de la Tabla Dinámica: cada usuario guarda las combinaciones de
 * filas / columnas / métricas / filtros / agregación que le sirven, y puede
 * marcar una como "por defecto" (se aplica al abrir el reporte).
 *
 * Persistencia: localStorage por reporte (`pivot-fav:<key>`) — por dispositivo,
 * igual que el resto de preferencias de la app. Las mutaciones usan updates
 * FUNCIONALES (dos operaciones seguidas no se pisan) y se persisten por efecto.
 */
export interface PivotConfig {
  filas: string[];
  columnas: string[];
  metricas: string[];
  filtrosDim: string[];
  agregacion: string;
}

export interface PivotFavorita {
  id: string;
  nombre: string;
  esDefault?: boolean;
  config: PivotConfig;
}

const claveDe = (storageKey: string) => `pivot-fav:${storageKey}`;

function leer(storageKey: string): PivotFavorita[] {
  if (!storageKey) return [];
  try {
    const raw = localStorage.getItem(claveDe(storageKey));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? (arr as PivotFavorita[]) : [];
  } catch {
    return [];
  }
}

/** Config de la favorita marcada por defecto (o null). Para inicializar el pivote. */
export function configPorDefecto(storageKey: string): PivotConfig | null {
  return leer(storageKey).find((f) => f.esDefault)?.config ?? null;
}

export interface FavoritasApi {
  favoritas: PivotFavorita[];
  guardar: (nombre: string, config: PivotConfig) => void;
  borrar: (id: string) => void;
  /** Marca `id` como default (y desmarca las demás); si ya lo era, lo desmarca. */
  alternarDefault: (id: string) => void;
}

export function usePivotFavoritas(storageKey: string): FavoritasApi {
  const [favoritas, setFavoritas] = useState<PivotFavorita[]>(() => leer(storageKey));

  // Persistir ante cualquier cambio (no en el mount inicial se escribe el mismo
  // valor leído, lo cual es inofensivo). Sin storageKey, no persiste.
  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(claveDe(storageKey), JSON.stringify(favoritas));
    } catch {
      // localStorage no disponible — se ignora.
    }
  }, [storageKey, favoritas]);

  const guardar = useCallback((nombre: string, config: PivotConfig) => {
    const limpio = nombre.trim();
    if (!limpio) return;
    setFavoritas((prev) => {
      // Mismo nombre → se sobrescribe su config.
      const existente = prev.find((f) => f.nombre.toLowerCase() === limpio.toLowerCase());
      if (existente) return prev.map((f) => (f.id === existente.id ? { ...f, config } : f));
      const nueva: PivotFavorita = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        nombre: limpio,
        config,
      };
      return [...prev, nueva];
    });
  }, []);

  const borrar = useCallback((id: string) => {
    setFavoritas((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const alternarDefault = useCallback((id: string) => {
    setFavoritas((prev) => {
      const yaEra = prev.find((f) => f.id === id)?.esDefault;
      return prev.map((f) => ({ ...f, esDefault: f.id === id ? !yaEra : false }));
    });
  }, []);

  return useMemo(
    () => ({ favoritas, guardar, borrar, alternarDefault }),
    [favoritas, guardar, borrar, alternarDefault],
  );
}
